"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionLinkState = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const page_state_1 = require("./page.state");
/**
 * State for linking a collection to a vector field.
 *
 * Modified target collections are tracked in a map keyed by collection ID.
 * Each target's proxy state `isDirty` determines whether it needs saving.
 */
class CollectionLinkState {
    constructor() {
        this.fieldId = null;
        this.collectionRevision = null;
        this.sourceFormRevision = null;
        this.isSpanLinkContext = false;
        this.modifiedTargetCollections = mobx_1.observable.map({}, { deep: false });
    }
    get isLinking() {
        return this.fieldId != null;
    }
    get hasModifiedTargets() {
        return this.modifiedTargetCollections.size > 0;
    }
    get hasDirtyTargets() {
        for (const [, entry] of this.modifiedTargetCollections) {
            if (entry.collection.isEditing && entry.collection.isDirty) {
                return true;
            }
        }
        return false;
    }
    beginLinking(fieldId, options) {
        var _a, _b;
        page_state_1.CurrentState.cancelInteractions();
        this.fieldId = fieldId;
        this.sourceFormRevision = (_a = options === null || options === void 0 ? void 0 : options.formRevision) !== null && _a !== void 0 ? _a : null;
        this.isSpanLinkContext = (_b = options === null || options === void 0 ? void 0 : options.isSpanContext) !== null && _b !== void 0 ? _b : false;
    }
    cancelLinking() {
        this.fieldId = null;
        this.collectionRevision = null;
        this.sourceFormRevision = null;
        this.isSpanLinkContext = false;
    }
    completeLinking(collection) {
        this.fieldId = null;
        this.collectionRevision = collection.revision;
    }
    /**
     * Register a target collection as modified.
     * If an entry already exists, the sourceCollectionId is added to the set.
     */
    registerModifiedTarget(collection, sourceCollectionId) {
        const existing = this.modifiedTargetCollections.get(collection.id);
        if (existing) {
            existing.sourceCollectionIds.add(sourceCollectionId);
        }
        else {
            this.modifiedTargetCollections.set(collection.id, {
                collection,
                sourceCollectionIds: new Set([sourceCollectionId]),
            });
        }
    }
    /**
     * Remove a modified target entry. Calls finishEdit if still editing.
     */
    removeModifiedTarget(collectionId) {
        const entry = this.modifiedTargetCollections.get(collectionId);
        if (entry) {
            if (entry.collection.isEditing) {
                entry.collection.finishEdit();
            }
            this.modifiedTargetCollections.delete(collectionId);
        }
    }
    /**
     * Remove a source's claim on modified targets.
     * If no claims remain, remove the entry from the map.
     * Does NOT call finishEdit — the topo editor manages each collection's
     * edit state through its own controllers.
     */
    cleanupTargetsForSource(sourceCollectionId) {
        const toRemove = [];
        for (const [collectionId, entry] of this.modifiedTargetCollections) {
            entry.sourceCollectionIds.delete(sourceCollectionId);
            if (entry.sourceCollectionIds.size === 0) {
                toRemove.push(collectionId);
            }
        }
        for (const id of toRemove) {
            this.modifiedTargetCollections.delete(id);
        }
    }
    /**
     * Discard all modified targets, reverting each collection's edit state.
     * Called when the user discards changes on the source collection.
     */
    discardAllModifiedTargets() {
        for (const [, entry] of this.modifiedTargetCollections) {
            if (entry.collection.isEditing) {
                entry.collection.finishEdit();
            }
        }
        this.modifiedTargetCollections.clear();
    }
    /**
     * Clear all modified target entries without reverting edit state.
     * Used after successful save — the proxy state has been serialized.
     */
    clearModifiedTargets() {
        this.modifiedTargetCollections.clear();
    }
    /**
     * Returns the list of modified target collections that are dirty and need saving.
     */
    getModifiedTargetCollections() {
        const result = [];
        for (const [, entry] of this.modifiedTargetCollections) {
            if (entry.collection.isEditing && entry.collection.isDirty) {
                result.push(entry.collection);
            }
        }
        return result;
    }
}
exports.CollectionLinkState = CollectionLinkState;
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "fieldId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "collectionRevision", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "sourceFormRevision", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "isSpanLinkContext", void 0);
tslib_1.__decorate([
    mobx_1.computed
], CollectionLinkState.prototype, "isLinking", null);
tslib_1.__decorate([
    mobx_1.computed
], CollectionLinkState.prototype, "hasModifiedTargets", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "beginLinking", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "cancelLinking", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "completeLinking", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "registerModifiedTarget", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "removeModifiedTarget", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "cleanupTargetsForSource", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "discardAllModifiedTargets", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "clearModifiedTargets", null);
//# sourceMappingURL=collection.link.state.js.map